import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import apiClient from '../api/client'
import {
  AlertOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  ApiOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  BookOutlined,
  BranchesOutlined,
  CheckSquareOutlined,
  CloudServerOutlined,
  CommentOutlined,
  CreditCardOutlined,
  FileProtectOutlined,
  KeyOutlined,
  HeartOutlined,
  InboxOutlined,
  DashboardOutlined,
  DollarCircleOutlined,
  FileTextOutlined,
  GiftOutlined,
  GlobalOutlined,
  ImportOutlined,
  MailOutlined,
  MessageOutlined,
  RadarChartOutlined,
  RightOutlined,
  SafetyOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
  ControlOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NC_VERSION } from '../constants/version'
import { useBranding } from '../stores/branding'

interface AdminSidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavLeaf {
  kind: 'leaf'
  path: string
  labelKey: string
  fallback: string
  icon?: ReactNode
  badge?: 'tasks' | 'tickets' | 'notifications'
  requireDns?: true
  requireMonitoring?: true
  requireDatacenter?: true
  requireLicense?: true
}

interface NavParent {
  kind: 'parent'
  key: string
  labelKey: string
  fallback: string
  icon: ReactNode
  badge?: 'tickets'
  children: NavLeaf[]
}

type NavEntry = NavLeaf | NavParent

interface NavSection {
  labelKey: string
  fallback: string
  entries: NavEntry[]
}

export const SECTIONS: NavSection[] = [
  {
    labelKey: 'sidebar.sections.overview',
    fallback: 'Übersicht',
    entries: [
      { kind: 'leaf', path: '/', labelKey: 'nav.dashboard', fallback: 'Dashboard', icon: <DashboardOutlined /> },
      { kind: 'leaf', path: '/tasks', labelKey: 'nav.tasks', fallback: 'Aufgaben', icon: <CheckSquareOutlined />, badge: 'tasks' },
      { kind: 'leaf', path: '/admin-notifications', labelKey: 'nav.notifications', fallback: 'Benachrichtigungen', icon: <BellOutlined />, badge: 'notifications' },
      { kind: 'leaf', path: '/reports', labelKey: 'nav.reports', fallback: 'Reports', icon: <BarChartOutlined /> },
      { kind: 'leaf', path: '/billing-automation', labelKey: 'nav.billingAutomation', fallback: 'Abrechnungs-Automatik', icon: <ControlOutlined /> },
    ],
  },
  {
    labelKey: 'sidebar.sections.clients',
    fallback: 'Kunden & Vertrieb',
    entries: [
      { kind: 'leaf', path: '/customers', labelKey: 'nav.customers', fallback: 'Kunden', icon: <TeamOutlined /> },
      { kind: 'leaf', path: '/products', labelKey: 'nav.products', fallback: 'Produkte', icon: <ShoppingOutlined /> },
      { kind: 'leaf', path: '/catalog-settings', labelKey: 'nav.catalogSettings', fallback: 'Katalog-Einstellungen', icon: <SettingOutlined /> },
      { kind: 'leaf', path: '/subscriptions', labelKey: 'nav.subscriptions', fallback: 'Abonnements', icon: <CloudServerOutlined /> },
      { kind: 'leaf', path: '/contracts', labelKey: 'nav.contracts', fallback: 'Laufzeitverträge', icon: <FileProtectOutlined /> },
      { kind: 'leaf', path: '/cancel-requests', labelKey: 'nav.cancelRequests', fallback: 'Kündigungen', icon: <AlertOutlined /> },
      { kind: 'leaf', path: '/terminate-reviews', labelKey: 'nav.terminateReviews', fallback: 'Terminierungs-Freigaben', icon: <SafetyCertificateOutlined /> },
      { kind: 'leaf', path: '/orders', labelKey: 'nav.orders', fallback: 'Bestellungen', icon: <ShoppingCartOutlined /> },
      { kind: 'leaf', path: '/affiliates', labelKey: 'nav.affiliates', fallback: 'Affiliates', icon: <GiftOutlined /> },
      { kind: 'leaf', path: '/reseller', labelKey: 'nav.reseller', fallback: 'Reseller', icon: <ShopOutlined /> },
    ],
  },
  {
    labelKey: 'sidebar.sections.billing',
    fallback: 'Billing',
    entries: [
      {
        kind: 'parent',
        key: 'billing-docs',
        labelKey: 'nav.group.billingDocs',
        fallback: 'Dokumente',
        icon: <FileTextOutlined />,
        children: [
          { kind: 'leaf', path: '/quotes', labelKey: 'nav.quotes', fallback: 'Angebote' },
          { kind: 'leaf', path: '/invoices', labelKey: 'nav.invoices', fallback: 'Rechnungen' },
          { kind: 'leaf', path: '/credit-notes', labelKey: 'nav.creditNotes', fallback: 'Gutschriften' },
          { kind: 'leaf', path: '/coupons', labelKey: 'nav.coupons', fallback: 'Coupons' },
        ],
      },
      {
        kind: 'parent',
        key: 'billing-payments',
        labelKey: 'nav.group.billingPayments',
        fallback: 'Zahlungsverkehr',
        icon: <CreditCardOutlined />,
        children: [
          { kind: 'leaf', path: '/payments', labelKey: 'nav.payments', fallback: 'Zahlungen' },
          { kind: 'leaf', path: '/sepa-mandates', labelKey: 'nav.sepaMandates', fallback: 'SEPA & Einzug' },
          { kind: 'leaf', path: '/banking', labelKey: 'nav.banking', fallback: 'Banking / Abgleich' },
          { kind: 'leaf', path: '/camt-import', labelKey: 'nav.camtImport', fallback: 'CAMT-Import' },
        ],
      },
      {
        kind: 'parent',
        key: 'billing-accounting',
        labelKey: 'nav.group.billingAccounting',
        fallback: 'Buchhaltung',
        icon: <BookOutlined />,
        children: [
          { kind: 'leaf', path: '/dunning', labelKey: 'nav.dunning', fallback: 'Mahnwesen & DATEV' },
          { kind: 'leaf', path: '/ledger', labelKey: 'nav.ledger', fallback: 'Ledger' },
          { kind: 'leaf', path: '/expenses', labelKey: 'nav.expenses', fallback: 'Betriebskosten', icon: <DollarCircleOutlined /> },
        ],
      },
    ],
  },
  {
    labelKey: 'sidebar.sections.support',
    fallback: 'Support',
    entries: [
      { kind: 'leaf', path: '/tickets', labelKey: 'nav.tickets', fallback: 'Tickets', icon: <CommentOutlined />, badge: 'tickets' },
      { kind: 'leaf', path: '/ticket-mailboxes', labelKey: 'nav.ticketMailboxes', fallback: 'Mailboxen', icon: <InboxOutlined /> },
      { kind: 'leaf', path: '/ticket-departments', labelKey: 'nav.ticketDepartments', fallback: 'Abteilungen', icon: <BranchesOutlined /> },
      { kind: 'leaf', path: '/predefined-replies', labelKey: 'nav.predefinedReplies', fallback: 'Antwort-Vorlagen', icon: <MessageOutlined /> },
      { kind: 'leaf', path: '/sla-policies', labelKey: 'nav.slaPolicies', fallback: 'SLA-Richtlinien', icon: <SafetyOutlined /> },
    ],
  },
  {
    labelKey: 'sidebar.sections.operations',
    fallback: 'Operations',
    entries: [
      {
        kind: 'parent',
        key: 'ops-provisioning',
        labelKey: 'nav.group.provisioning',
        fallback: 'Provisioning',
        icon: <ApiOutlined />,
        children: [
          { kind: 'leaf', path: '/provisioning-jobs', labelKey: 'nav.provJobs', fallback: 'Jobs' },
          { kind: 'leaf', path: '/provisioning-servers', labelKey: 'nav.provServers', fallback: 'Server' },
          { kind: 'leaf', path: '/service-instances', labelKey: 'nav.serviceInstances', fallback: 'Instanzen' },
          { kind: 'leaf', path: '/server-management', labelKey: 'nav.serverMgmt', fallback: 'Server-Management' },
        ],
      },
      {
        kind: 'parent',
        key: 'ops-communication',
        labelKey: 'nav.group.communication',
        fallback: 'Kommunikation',
        icon: <MailOutlined />,
        children: [
          { kind: 'leaf', path: '/mass-mails', labelKey: 'nav.massMails', fallback: 'Mass-Mail' },
          { kind: 'leaf', path: '/newsletter', labelKey: 'nav.newsletter', fallback: 'Newsletter' },
          { kind: 'leaf', path: '/mail-log', labelKey: 'nav.mailLog', fallback: 'Mail-Log' },
          { kind: 'leaf', path: '/webhooks', labelKey: 'nav.webhooks', fallback: 'Webhooks' },
        ],
      },
      {
        kind: 'parent',
        key: 'ops-infra',
        labelKey: 'nav.group.infra',
        fallback: 'Infrastruktur',
        icon: <GlobalOutlined />,
        children: [
          { kind: 'leaf', path: '/domains', labelKey: 'nav.domains', fallback: 'Domains' },
          { kind: 'leaf', path: '/ssl', labelKey: 'nav.ssl', fallback: 'SSL-Zertifikate' },
          { kind: 'leaf', path: '/dns', labelKey: 'nav.dns', fallback: 'DNS-Zonen' },
          { kind: 'leaf', path: '/usage-records', labelKey: 'nav.usageRecords', fallback: 'Datenverbrauch' },
        ],
      },
      {
        kind: 'parent',
        key: 'ops-monitoring',
        labelKey: 'nav.monitoringV2',
        fallback: 'Monitoring',
        icon: <RadarChartOutlined />,
        children: [
          { kind: 'leaf', path: '/monitoring-v2/dashboard', labelKey: 'nav.monDashboard', fallback: 'Dashboard', icon: <DashboardOutlined />, requireMonitoring: true },
          { kind: 'leaf', path: '/monitoring-v2/hosts', labelKey: 'nav.monHosts', fallback: 'Hosts', icon: <CloudServerOutlined />, requireMonitoring: true },
          { kind: 'leaf', path: '/monitoring-v2/satellites', labelKey: 'nav.monSatellites', fallback: 'Satelliten', icon: <RadarChartOutlined />, requireMonitoring: true },
          { kind: 'leaf', path: '/monitoring-v2/alerts', labelKey: 'nav.monAlerts', fallback: 'Alerts', icon: <AlertOutlined />, requireMonitoring: true },
          { kind: 'leaf', path: '/monitoring-v2/heartbeats', labelKey: 'nav.monHeartbeats', fallback: 'Heartbeats', icon: <HeartOutlined />, requireMonitoring: true },
          { kind: 'leaf', path: '/monitoring-v2/channels', labelKey: 'nav.monChannels', fallback: 'Benachrichtigungskanäle', icon: <BellOutlined />, requireMonitoring: true },
          { kind: 'leaf', path: '/monitoring-v2/config', labelKey: 'nav.monConfig', fallback: 'Konfiguration', icon: <SettingOutlined />, requireMonitoring: true },
        ],
      },
    ],
  },
  {
    labelKey: 'sidebar.sections.modules',
    fallback: 'Module',
    entries: [
      { kind: 'leaf', path: '/datacenter', labelKey: 'nav.datacenter', fallback: 'Datacenter', icon: <CloudServerOutlined />, requireDatacenter: true },
      { kind: 'leaf', path: '/ip-management', labelKey: 'nav.ipManagement', fallback: 'IP-Verwaltung', icon: <GlobalOutlined />, requireDatacenter: true },
      { kind: 'leaf', path: '/licenses', labelKey: 'nav.licenses', fallback: 'Lizenzen (WebPros)', icon: <KeyOutlined />, requireLicense: true },
      { kind: 'leaf', path: '/license-server', labelKey: 'nav.licenseServer', fallback: 'Eigener Lizenzserver', icon: <SafetyOutlined />, requireLicense: true },
    ],
  },
  {
    labelKey: 'sidebar.sections.system',
    fallback: 'System',
    entries: [
      { kind: 'leaf', path: '/extensions', labelKey: 'nav.extensions', fallback: 'Extensions', icon: <AppstoreOutlined /> },
      {
        kind: 'parent',
        key: 'sys-content',
        labelKey: 'nav.group.content',
        fallback: 'Inhalte',
        icon: <BookOutlined />,
        children: [
          { kind: 'leaf', path: '/kb', labelKey: 'nav.kb', fallback: 'Knowledge Base' },
          { kind: 'leaf', path: '/announcements', labelKey: 'nav.announcements', fallback: 'Ankündigungen' },
          { kind: 'leaf', path: '/status-page', labelKey: 'nav.statusPage', fallback: 'Status-Page' },
        ],
      },
      {
        kind: 'parent',
        key: 'sys-compliance',
        labelKey: 'nav.group.compliance',
        fallback: 'Compliance',
        icon: <SafetyOutlined />,
        children: [
          { kind: 'leaf', path: '/dsgvo', labelKey: 'nav.dsgvo', fallback: 'DSGVO' },
          { kind: 'leaf', path: '/dsgvo-requests', labelKey: 'nav.dsgvoRequests', fallback: 'DSGVO-Anfragen' },
          { kind: 'leaf', path: '/audit-log', labelKey: 'nav.auditLog', fallback: 'Audit-Log' },
        ],
      },
      { kind: 'leaf', path: '/classic-import', labelKey: 'nav.classicImport', fallback: 'Altsystem-Import', icon: <ImportOutlined /> },
      { kind: 'leaf', path: '/portal-users', labelKey: 'nav.portalUsers', fallback: 'Portal-Nutzer', icon: <UserOutlined /> },
      { kind: 'leaf', path: '/team', labelKey: 'nav.team', fallback: 'Team & Rollen', icon: <TeamOutlined /> },
      { kind: 'leaf', path: '/backups', labelKey: 'nav.backups', fallback: 'Backups', icon: <DatabaseOutlined /> },
      { kind: 'leaf', path: '/settings', labelKey: 'nav.settings', fallback: 'Einstellungen', icon: <SettingOutlined /> },
    ],
  },
]

function isLeafActive(path: string, pathname: string) {
  return path === '/' ? pathname === '/' : pathname.startsWith(path)
}

function getAutoExpanded(pathname: string): Set<string> {
  const keys = new Set<string>()
  for (const section of SECTIONS) {
    for (const entry of section.entries) {
      if (entry.kind === 'parent' && entry.children.some(c => isLeafActive(c.path, pathname))) {
        keys.add(entry.key)
      }
    }
  }
  return keys
}

// allLeafPaths returns every leaf path in a section (including parent
// children) — used to detect which area the current route belongs to.
function sectionLeafPaths(section: NavSection): string[] {
  const out: string[] = []
  for (const e of section.entries) {
    if (e.kind === 'leaf') out.push(e.path)
    else e.children.forEach(c => out.push(c.path))
  }
  return out
}

// getFirstLeafPath returns the landing route for an area (first leaf,
// descending into the first parent group if needed).
export function getFirstLeafPath(section: NavSection): string {
  const first = section.entries[0]
  if (!first) return '/'
  return first.kind === 'leaf' ? first.path : (first.children[0]?.path ?? '/')
}

// getActiveLeafPath returns the registered leaf (list/landing) path the current
// route belongs to via longest-prefix match, e.g. "/customers/591" → "/customers".
// Empty when nothing matches. Used so switching areas resumes on the LIST page,
// never on a specific detail record.
export function getActiveLeafPath(pathname: string): string {
  let best = ''
  for (const s of SECTIONS) {
    for (const p of sectionLeafPaths(s)) {
      const matches = p === '/' ? pathname === '/' : pathname.startsWith(p)
      if (matches && p.length > best.length) best = p
    }
  }
  return best
}

// getActiveSectionIndex finds the area the current route belongs to via the
// longest matching leaf-path prefix. Defaults to the first area.
export function getActiveSectionIndex(pathname: string): number {
  let best = 0
  let bestLen = -1
  SECTIONS.forEach((s, i) => {
    for (const p of sectionLeafPaths(s)) {
      const matches = p === '/' ? pathname === '/' : pathname.startsWith(p)
      if (matches && p.length > bestLen) {
        bestLen = p.length
        best = i
      }
    }
  })
  return best
}

// NavFlags carry the runtime module/extension state that gates nav entries.
export interface NavFlags {
  dns: boolean
  monitoring: boolean
  datacenter: boolean
  license: boolean
}

// isLeafVisible is the single source of truth for whether a nav leaf shows,
// given the active module/extension flags. Used for top-level leaves, parent
// children, and (in AreaNav) whether a whole area has anything to show.
export function isLeafVisible(c: NavLeaf, f: NavFlags): boolean {
  return (!c.requireDns || f.dns) && (!c.requireMonitoring || f.monitoring) &&
    (!c.requireDatacenter || f.datacenter) && (!c.requireLicense || f.license)
}

// FlatNavItem is one navigable leaf, flattened across sections/groups — used by
// the global command palette (Cmd/Ctrl-K).
export interface FlatNavItem {
  path: string
  labelKey: string
  fallback: string
  sectionLabelKey: string
  sectionFallback: string
}

const ALL_FLAGS: NavFlags = { dns: true, monitoring: true, datacenter: true, license: true }

// flattenNavLeaves returns every navigable leaf (top-level + group children)
// across all sections. flags default to all-enabled so the palette can jump
// anywhere; pass real flags to hide disabled-module pages.
export function flattenNavLeaves(flags: NavFlags = ALL_FLAGS): FlatNavItem[] {
  const out: FlatNavItem[] = []
  for (const s of SECTIONS) {
    for (const e of s.entries) {
      if (e.kind === 'leaf') {
        if (isLeafVisible(e, flags)) {
          out.push({ path: e.path, labelKey: e.labelKey, fallback: e.fallback, sectionLabelKey: s.labelKey, sectionFallback: s.fallback })
        }
      } else {
        for (const c of e.children) {
          if (isLeafVisible(c, flags)) {
            out.push({ path: c.path, labelKey: c.labelKey, fallback: c.fallback, sectionLabelKey: s.labelKey, sectionFallback: s.fallback })
          }
        }
      }
    }
  }
  return out
}

// allParentKeys returns every collapsible group key across all sections — the
// flat sidebar starts with all of them expanded.
function allParentKeys(): string[] {
  const keys: string[] = []
  for (const s of SECTIONS) {
    for (const e of s.entries) {
      if (e.kind === 'parent') keys.push(e.key)
    }
  }
  return keys
}

// sectionVisibleCount counts entries (leaves + parents with ≥1 visible child)
// that would render in a section — so AreaNav can hide empty areas.
export function sectionVisibleCount(section: NavSection, f: NavFlags): number {
  let n = 0
  for (const e of section.entries) {
    if (e.kind === 'leaf') { if (isLeafVisible(e, f)) n++ }
    else if (e.children.some(c => isLeafVisible(c, f))) n++
  }
  return n
}

interface ExtRow { id: string; enabled: boolean; state: string }

export default function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const branding = useBranding()

  // Flat sidebar: every area + group is visible at once (PHP-style). Sub-groups
  // start expanded so nothing is hidden; the operator can still collapse any.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(allParentKeys()))

  // On navigation, ensure the active group is open — additive, never closes
  // the others (no accordion).
  useEffect(() => {
    const active = getAutoExpanded(location.pathname)
    if (active.size === 0) return
    setExpanded(prev => {
      const next = new Set(prev)
      active.forEach(k => next.add(k))
      return next
    })
  }, [location.pathname])

  // Keep the active item in view: the flat sidebar is tall, so on navigation
  // scroll the highlighted entry into the visible area (no-op if already shown).
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = navRef.current?.querySelector(
      '.sidebar-menu-item.active, .sidebar-child-item.active',
    ) as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  }, [location.pathname])

  // Independent toggle: collapse/expand a single group, others untouched.
  const toggle = useCallback((key: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // Collapsible top-level categories (areas): each section can be folded so the
  // flat list stays usable, especially on mobile. State persists per browser;
  // default = only the active area open.
  const activeSectionKey = SECTIONS[getActiveSectionIndex(location.pathname)]?.labelKey
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('nc_nav_open_sections') || 'null')
      if (Array.isArray(saved)) return new Set(saved as string[])
    } catch { /* ignore */ }
    return new Set(activeSectionKey ? [activeSectionKey] : [])
  })
  const toggleSection = useCallback((key: string) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      localStorage.setItem('nc_nav_open_sections', JSON.stringify([...next]))
      return next
    })
  }, [])
  // Always keep the area of the current page open.
  useEffect(() => {
    if (!activeSectionKey) return
    setOpenSections(prev => (prev.has(activeSectionKey) ? prev : new Set(prev).add(activeSectionKey)))
  }, [activeSectionKey])


  // Pinned favourites (persisted per browser) — quick-access shortcuts on top.
  const [favs, setFavs] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('nc_nav_favs') || '[]') } catch { return [] }
  })
  const toggleFav = useCallback((path: string) => {
    setFavs(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
      localStorage.setItem('nc_nav_favs', JSON.stringify(next))
      return next
    })
  }, [])

  const openTasksQ = useQuery({
    queryKey: ['operator-tasks-count-open'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { count: number } }>('/api/v1/operator-tasks/count-open')
      return res.data.data.count
    },
    refetchInterval: 60_000,
    retry: false,
  })

  const unreadNotifsQ = useQuery({
    queryKey: ['notif-count'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { unread: number } }>('/api/v1/notifications/count')
      return res.data.data.unread
    },
    refetchInterval: 60_000,
    retry: false,
  })

  const openTicketsQ = useQuery({
    queryKey: ['tickets-count-open'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { count: number } }>('/api/v1/tickets/count-open')
      return res.data.data.count
    },
    refetchInterval: 60_000,
    retry: false,
  })

  const extensionsQ = useQuery({
    queryKey: ['extensions-sidebar'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ExtRow[] }>('/api/v1/extensions')
      return res.data.data || []
    },
    staleTime: 30_000,
    retry: false,
  })
  const dnsActive = extensionsQ.data?.some(
    e => e.id === 'powerdns-backend' && e.enabled,
  ) ?? false
  const monitoringActive = extensionsQ.data?.some(
    e => e.id === 'monitoring' && e.enabled,
  ) ?? false

  const moduleStateQ = useQuery({
    queryKey: ['module-state-sidebar'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { datacenter_enabled: boolean; license_enabled: boolean } }>('/api/v1/module-state')
      return res.data.data
    },
    staleTime: 30_000,
    retry: false,
  })
  // Default to enabled until known, so the nav doesn't flicker-hide.
  const datacenterActive = moduleStateQ.data?.datacenter_enabled ?? true
  const licenseActive = moduleStateQ.data?.license_enabled ?? true

  const logoURL = branding.data?.logo_url
  const productName = branding.data?.product_name || t('app.name', 'NetCell Control')

  const renderLeaf = (item: NavLeaf, isChild = false) => {
    const active = isLeafActive(item.path, location.pathname)
    const cls = isChild ? 'sidebar-child-item' : 'sidebar-menu-item'
    const label = t(item.labelKey, item.fallback)
    const isFav = favs.includes(item.path)
    return (
      <li key={item.path} className={`${cls}${active ? ' active' : ''}`}>
        <Link to={item.path} onClick={onClose} title={label}>
          {!isChild && item.icon}
          <span>{label}</span>
          {item.badge === 'tasks' && (openTasksQ.data ?? 0) > 0 && (
            <span className="sidebar-badge">{openTasksQ.data}</span>
          )}
          {item.badge === 'tickets' && (openTicketsQ.data ?? 0) > 0 && (
            <span className="sidebar-badge">{openTicketsQ.data}</span>
          )}
          {item.badge === 'notifications' && (unreadNotifsQ.data ?? 0) > 0 && (
            <span className="sidebar-badge">{unreadNotifsQ.data}</span>
          )}
        </Link>
        <button
          type="button"
          className={`sidebar-fav-btn${isFav ? ' on' : ''}`}
          title={isFav ? t('nav.unpin', 'Aus Favoriten entfernen') : t('nav.pin', 'Zu Favoriten')}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(item.path) }}
        >
          {isFav ? <StarFilled /> : <StarOutlined />}
        </button>
      </li>
    )
  }

  const navFlags: NavFlags = { dns: dnsActive, monitoring: monitoringActive, datacenter: datacenterActive, license: licenseActive }

  const favItems = useMemo(() => {
    if (favs.length === 0) return [] as FlatNavItem[]
    const flat = flattenNavLeaves(navFlags)
    return favs
      .map(p => flat.find(f => f.path === p))
      .filter((f): f is FlatNavItem => !!f)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favs, dnsActive, monitoringActive, datacenterActive, licenseActive])

  const renderEntry = (entry: NavEntry) => {
    if (entry.kind === 'leaf') return renderLeaf(entry)

    const isExpanded = expanded.has(entry.key)
    const visibleChildren = entry.children.filter(c => isLeafVisible(c, navFlags))
    if (visibleChildren.length === 0) return null

    const hasActive = visibleChildren.some(c => isLeafActive(c.path, location.pathname))

    return (
      <li key={entry.key} className={`sidebar-parent-item${isExpanded ? ' expanded' : ''}${hasActive ? ' has-active' : ''}`}>
        <button
          title={t(entry.labelKey, entry.fallback)}
          onClick={() => toggle(entry.key)}
        >
          {entry.icon}
          <span>{t(entry.labelKey, entry.fallback)}</span>
          {entry.badge === 'tickets' && (openTicketsQ.data ?? 0) > 0 && (
            <span className="sidebar-badge">{openTicketsQ.data}</span>
          )}
          <RightOutlined className="sidebar-parent-chevron" />
        </button>
        <ul className={`sidebar-children${isExpanded ? ' open' : ''}`}>
          {visibleChildren.map(c => renderLeaf(c, true))}
        </ul>
      </li>
    )
  }

  return (
    <nav ref={navRef} className={`sidebar${isOpen ? ' open' : ''}`}>
      <div className="sidebar-logo">
        {logoURL
          ? <img src={logoURL} alt={productName} className="sidebar-logo-img" />
          : (<>
              <div className="sidebar-logo-icon">N</div>
              <div className="sidebar-logo-text">{productName}</div>
            </>)}
      </div>

      {favItems.length > 0 && (
        <div className="sidebar-favs">
          <div className="sidebar-section">
            <div className="sidebar-section-label">{t('nav.favorites', 'Favoriten')}</div>
          </div>
          <ul className="sidebar-menu">
            {favItems.map(f => {
              const active = isLeafActive(f.path, location.pathname)
              const label = t(f.labelKey, f.fallback)
              return (
                <li key={`fav-${f.path}`} className={`sidebar-menu-item${active ? ' active' : ''}`}>
                  <Link to={f.path} onClick={onClose} title={label}>
                    <StarFilled className="sidebar-fav-lead" />
                    <span>{label}</span>
                  </Link>
                  <button
                    type="button"
                    className="sidebar-fav-btn on"
                    title={t('nav.unpin', 'Aus Favoriten entfernen')}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(f.path) }}
                  >
                    <StarFilled />
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Flat sidebar with collapsible categories: every area is listed, but
          each can be folded (click its header) so the list stays usable —
          especially on mobile. The active area is always open. */}
      {SECTIONS.map((section) => {
        if (sectionVisibleCount(section, navFlags) === 0) return null
        const open = openSections.has(section.labelKey)
        return (
          <div key={section.labelKey} className={`sidebar-area${open ? ' open' : ''}`}>
            <button
              type="button"
              className="sidebar-section sidebar-section-toggle"
              aria-expanded={open}
              onClick={() => toggleSection(section.labelKey)}
            >
              <span className="sidebar-section-label">
                {t(section.labelKey, section.fallback)}
              </span>
              <RightOutlined className="sidebar-section-chevron" />
            </button>
            {open && (
              <ul className="sidebar-menu">
                {section.entries
                  .filter(en => en.kind !== 'leaf' || isLeafVisible(en, navFlags))
                  .map(renderEntry)}
              </ul>
            )}
          </div>
        )
      })}

      <div className="sidebar-version">v{NC_VERSION}</div>
    </nav>
  )
}
